import { useEffect, useState } from 'react';
import {
  FormControlLabel,
  FormGroup,
  Radio,
  RadioGroup,
  Stack,
  Switch
} from '@mui/material';
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { yupResolver } from '@hookform/resolvers/yup';
import { add } from 'date-fns';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext
} from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';
import Checkbox from 'components/Checkbox';
import Select, { SelectOption } from 'components/Select';
import Text from 'components/Text';
import TextField, {
  LimitedTextField,
  NumberTextField
} from 'components/TextField';
import { useAppContext } from 'hooks/useAppContext';
import { useConfigContext } from 'hooks/useConfigContext';
import { useCampaignById } from 'queries/UseCampaigns';
import {
  BlockTypes,
  GoalMeterType,
  GoalSource,
  IGivingFormConfig,
  IGoalMeterBlock
} from 'types';
import './EditGoalMeter.scss';

const sectionTitleMaxChars = 50;

const goalMeterSchema = yup.object({
  goalMeter: yup.object({
    sectionTitle: yup
      .string()
      .max(
        sectionTitleMaxChars,
        `Section Title cannot exceed ${sectionTitleMaxChars} characters`
      ),
    goalMeterSource: yup.string().required('Required'),
    goalMeterType: yup.string().required('Required'),
    goalAmount: yup.number().required('Required').typeError('Required').min(1)
  })
});

const newGoalEndDate = add(new Date(), { days: 30 });

const emptyGoalMeter: IGoalMeterBlock = {
  id: '',
  blockType: BlockTypes.GoalMeterBlock,
  goalMeterType: GoalMeterType.PROGRESS,
  goalSource: GoalSource.CAMPAIGN,
  goalAmount: 1000,
  sectionTitle: '',
  goalEndDate: newGoalEndDate.toString(),
  showAmountRaised: false,
  showGoalAmount: false,
  showDaysLeft: false
};

const EditGoalMeter = () => {
  const { campaignId: paramCampaignId } = useParams();
  const { selectedOrganization, selectedCampaignName, selectedCampaignId } =
    useAppContext();
  const campaignId = paramCampaignId || selectedCampaignId;
  const organizationId = selectedOrganization?.id;
  const { data: campaignData } = useCampaignById(organizationId, campaignId);

  const { control } = useFormContext();
  const { configData, updateConfig } = useConfigContext<IGivingFormConfig>();
  const { config } = configData;

  const goalMeterBlock = config.blocks.find(
    (block) => block.blockType === BlockTypes.GoalMeterBlock
  ) as IGoalMeterBlock | undefined;
  const goalMeterBlockIndex = goalMeterBlock
    ? config.blocks.indexOf(goalMeterBlock)
    : -1;

  const [showGoalDateInput, setShowGoalDateInput] = useState<boolean>(false);

  useEffect(() => {
    if (goalMeterBlock) {
      setShowGoalDateInput(
        goalMeterBlock.showDaysLeft &&
          goalMeterBlock.goalSource === GoalSource.CHANNEL
      );
    }
  }, [goalMeterBlock]);

  const updateGoalMeterBlockConfig = (
    newGoalMeterBlockConfig: IGoalMeterBlock
  ) => {
    const newBlocks = [...config.blocks];

    if (goalMeterBlock && goalMeterBlockIndex >= 0) {
      newBlocks.splice(goalMeterBlockIndex, 1, newGoalMeterBlockConfig);
    } else {
      const newGoalMeterBlock: IGoalMeterBlock = {
        ...newGoalMeterBlockConfig,
        id: uuid(),
        blockType: BlockTypes.GoalMeterBlock
      };
      newBlocks.push(newGoalMeterBlock);
    }

    const newConfig: IGivingFormConfig = {
      ...config,
      blocks: newBlocks
    };

    updateConfig(newConfig);
  };

  const deleteGoalMeterBlock = () => {
    const newBlocks = [...config.blocks];

    if (goalMeterBlockIndex >= 0) {
      newBlocks.splice(goalMeterBlockIndex, 1);
    }

    const newConfig: IGivingFormConfig = {
      ...config,
      blocks: newBlocks
    };

    updateConfig(newConfig);
  };

  const handleEnableGoalMeter = () => {
    const newGoalMeterBlock: IGoalMeterBlock = {
      ...emptyGoalMeter
    };

    if (goalMeterBlock) {
      deleteGoalMeterBlock();
    } else {
      updateGoalMeterBlockConfig(newGoalMeterBlock);
    }
  };

  const goalMeterTypeOptions: SelectOption[] = [
    {
      label: 'Progress Bar',
      value: GoalMeterType.PROGRESS
    },
    {
      label: 'Speedometer',
      value: GoalMeterType.SPEEDOMETER
    },
    {
      label: 'Circle',
      value: GoalMeterType.CIRCLE
    }
  ];

  const getGoalMeterTypeRenderedValue = () => {
    const selection = goalMeterTypeOptions.find(
      (option) => option.value === goalMeterBlock?.goalMeterType
    );
    return (
      <Stack direction="row" spacing={0.5}>
        <Text variant="body">{selection?.label}</Text>
      </Stack>
    );
  };

  const handleGoalSourceOnChange = (value: GoalSource) => {
    if (value === GoalSource.CAMPAIGN && !campaignData?.endDate) {
      const updatedBlock = {
        ...goalMeterBlock,
        goalSource: value,
        showDaysLeft: false
      };
      updateGoalMeterBlockConfig(updatedBlock);
    } else {
      const updatedBlock = { ...goalMeterBlock, goalSource: value };
      updateGoalMeterBlockConfig(updatedBlock);
    }
  };

  const handleChange = (blockFieldName: string, content: any) => {
    const newGoalMeterBlock = {
      ...goalMeterBlock,
      [blockFieldName]: content
    };

    updateGoalMeterBlockConfig(newGoalMeterBlock);
  };

  return (
    <div className="element-library-edit-goal-meter">
      <FormControlLabel
        className="enable-goal-meter-switch"
        control={
          <Switch
            checked={Boolean(goalMeterBlock)}
            onChange={handleEnableGoalMeter}
            inputProps={{ 'aria-label': 'enable goal meter toggle switch' }}
          />
        }
        label="Enable Goal Meter"
      />
      <div className={goalMeterBlock ? '' : 'disabled'}>
        <div className="disabled-overlay" />
        <div className="goal-meter-editor-section-container">
          <Text className="goal-meter-editor-section-title" variant="h5">
            Section Title
          </Text>
          <Controller
            name="goalMeter.sectionTitle"
            control={control}
            defaultValue={goalMeterBlock?.sectionTitle ?? ''}
            render={({
              field: { ref, onChange, onBlur, ...field },
              fieldState: { error }
            }) => (
              <LimitedTextField
                {...field}
                hiddenLabel
                fullWidth
                multiline
                minRows={3}
                maxChar={sectionTitleMaxChars}
                error={!!error}
                value={goalMeterBlock?.sectionTitle ?? ''}
                onChange={(event) => {
                  onChange(
                    // note kstickel 12/14/22: MUI erroneously appends one additional character to end of value if user continues to
                    // type beyond maximum length; taking substring of value to remove 51st char if present
                    event.target.value.substring(0, sectionTitleMaxChars)
                  );
                  handleChange(
                    'sectionTitle',
                    event.target.value.substring(0, sectionTitleMaxChars)
                  );
                }}
                onBlur={() => {
                  onBlur();
                }}
              />
            )}
          />
        </div>

        <div className="goal-meter-editor-section-container">
          <Text variant="h5">Select Goal Meter</Text>
          <Controller
            name="goalMeter.goalMeterSource"
            control={control}
            render={({ field: { ref, onChange, ...field } }) => (
              <RadioGroup
                {...field}
                value={goalMeterBlock?.goalSource ?? GoalSource.CAMPAIGN}
                onChange={(event) => {
                  onChange(event.target.value);
                  handleGoalSourceOnChange(event.target.value as GoalSource);
                }}
              >
                <FormControlLabel
                  value={GoalSource.CAMPAIGN}
                  control={<Radio />}
                  label={
                    <Text variant="caption">
                      Campaign{' '}
                      {selectedCampaignName && (
                        <span className="goal-source-bold">
                          ({selectedCampaignName})
                        </span>
                      )}
                    </Text>
                  }
                />
                <FormControlLabel
                  value={GoalSource.CHANNEL}
                  control={<Radio />}
                  label={
                    <Text variant="caption">
                      Channel{' '}
                      {configData.name && (
                        <span className="goal-source-bold">
                          ({configData.name})
                        </span>
                      )}
                    </Text>
                  }
                />
              </RadioGroup>
            )}
          />
        </div>

        <div className="goal-meter-editor-section-container">
          <Text className="goal-meter-editor-section-title" variant="h5">
            Select Goal Meter Visual
          </Text>
          <Controller
            name="goalMeter.goalMeterType"
            control={control}
            defaultValue={
              goalMeterBlock?.goalMeterType ?? GoalMeterType.PROGRESS
            }
            render={({
              field: { ref, onChange, onBlur, ...field },
              fieldState: { error }
            }) => (
              <Select
                {...field}
                aria-label="goal meter visual"
                displayEmpty
                error={!!error}
                onBlur={() => onBlur()}
                onChange={(event) => {
                  onChange(event);
                  handleChange(
                    'goalMeterType',
                    event.target.value as GoalMeterType
                  );
                }}
                options={goalMeterTypeOptions}
                renderValue={getGoalMeterTypeRenderedValue}
                value={goalMeterBlock?.goalMeterType ?? GoalMeterType.PROGRESS}
              />
            )}
          />
        </div>
        <div className="goal-meter-editor-section-container">
          <Text className="goal-meter-editor-section-title" variant="h5">
            Goal Amount
          </Text>
          <Controller
            name="goalMeter.goalAmount"
            control={control}
            defaultValue={goalMeterBlock?.goalAmount ?? 1000}
            render={({
              field: { ref, onChange, onBlur, ...field },
              fieldState: { error }
            }) => (
              <NumberTextField
                {...field}
                aria-label="goal meter goal amount"
                decimalScale={0}
                error={!!error}
                fullWidth
                helperText={error && 'Required'}
                hiddenLabel
                onBlur={() => {
                  onBlur();
                }}
                onChange={(value) => {
                  onChange(value);
                  handleChange('goalAmount', Number(value));
                }}
                placeholder="$"
                prefix="$"
                thousandSeparator
              />
            )}
          />
        </div>
        <div className="goal-meter-editor-section-container">
          <Text variant="h4">Display Options</Text>
          <FormGroup className="goal-meter-display-options-container">
            <FormControlLabel
              label="Show Amount Raised"
              control={
                <Checkbox
                  checked={goalMeterBlock?.showAmountRaised ?? false}
                  onChange={(event, isChecked) => {
                    handleChange('showAmountRaised', isChecked);
                  }}
                />
              }
            />
            <FormControlLabel
              label="Show Goal Amount"
              control={
                <Checkbox
                  checked={goalMeterBlock?.showGoalAmount ?? false}
                  onChange={(event, isChecked) => {
                    handleChange('showGoalAmount', isChecked);
                  }}
                />
              }
            />
            <FormControlLabel // disable if campaign and no end date
              label="Show Days Left"
              control={
                <Checkbox
                  checked={goalMeterBlock?.showDaysLeft ?? false}
                  disabled={
                    goalMeterBlock?.goalSource === GoalSource.CAMPAIGN &&
                    !campaignData?.endDate
                  }
                  onChange={(event, isChecked) => {
                    handleChange('showDaysLeft', isChecked);
                    if (goalMeterBlock?.goalSource === GoalSource.CHANNEL) {
                      setShowGoalDateInput(isChecked);
                    }
                  }}
                />
              }
            />
          </FormGroup>
          {showGoalDateInput && (
            <div className="goal-date-time-container">
              <Text variant="h5">Goal End Date</Text>
              <DateTimePicker
                minDate={new Date()}
                minDateTime={new Date()}
                value={goalMeterBlock?.goalEndDate ?? newGoalEndDate.toString()}
                onChange={(value) => {
                  handleChange('goalEndDate', value.toString());
                }}
                renderInput={(props) => (
                  <TextField
                    {...props}
                    error={props.error}
                    helperText={props.error && 'End date cannot be in the past'}
                  />
                )}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default (): JSX.Element => {
  const methods = useForm({
    resolver: yupResolver(goalMeterSchema),
    mode: 'onChange'
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <FormProvider {...methods}>
        <EditGoalMeter />
      </FormProvider>
    </LocalizationProvider>
  );
};
